import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { useParams } from "react-router-dom";
import type { DraggableSyntheticListeners, UniqueIdentifier } from "@dnd-kit/core";
import type { Transform } from "@dnd-kit/utilities";
import {
	AnimatePresence,
	domAnimation,
	LazyMotion,
	motion
} from "framer-motion";

import EditableTextSlim from "../../../../../../general-ui/editable-text/editable-text-slim";
import EditableTextDraftQuill from "../../../../../../general-ui/editable-text/editable-text-draft-quill";
import { useAppDispatch, useTypedSelector } from "../../../../../../../store/reducers/use-typed-selector";
import { setActiveAccordionContainer, updateHomepageModule } from "../../../../../../../store/actions/admin/create-event";
import { AccordionItem, AccordionItemUpdate, ColorKeys, PageModule, TextOverrides } from "../../../../../../../types/working-model";
import Icon, { COLORS, ICONS } from "../../../../../../general-ui/icon";
import { useTranslate } from "../../../../../../../i18n/useTranslationModules";
import useTranslationRoute from "../../../../../../live-event/hooks/use-translation-route";

import styles from "../../../../../../../scss/live-event/base/modules/accordion modules/item.module.scss";
import { getStylingOverrides } from "../../../../../../live-event/utils";

export interface AccordionItemProps {
	dragOverlay?: boolean;
	color?: string;
	disabled?: boolean;
	dragging?: boolean;
	handle?: boolean;
	height?: number;
	index?: number;
	fadeIn?: boolean;
	transform?: Transform | null;
	listeners?: DraggableSyntheticListeners;
	sorting?: boolean;
	transition?: string | null;
	value: AccordionItem;
	onRemove?(removedItemId: UniqueIdentifier): void;
	containerId: UniqueIdentifier;
	hideRemoveButton?: boolean;
	onContentBlur?(props: AccordionItemUpdate): void;
	module?: PageModule;
}

const Item = React.memo(React.forwardRef<HTMLLIElement, AccordionItemProps>(({
	color,
	dragOverlay,
	dragging,
	disabled,
	fadeIn,
	handle,
	index,
	listeners,
	onRemove,
	sorting,
	transition,
	transform,
	value,
	containerId,
	hideRemoveButton,
	module,
	onContentBlur = () => null,
	...props
}, ref) => {

	const translationUpdates = useTypedSelector(event => event.LiveEventReducer.translationUpdates);

	const params = useParams<Record<string, string>>();
	const language = params?.language ?? 'en';

	const translationRoute = useTranslationRoute();
	const { t } = useTranslate([translationRoute, "homepage"]);

	const titleFallback = value?.title?.[language] ?? value?.title?.base ?? 'Question';
	const descriptionFallback = value?.description?.[language] ?? value?.description?.base ?? 'Answer';

	const _title = t(`${translationRoute}:accordion.${module?.id}.${value.id}.title`, titleFallback as string);
	const _description = t(`${translationRoute}:accordion.${module?.id}.${value.id}.description`, descriptionFallback as string);

	const [activeSection, setActiveSection] = useState<UniqueIdentifier[]>([]);
	const [title, setTitle] = useState<string>(_title);
	const [description, setDescription] = useState<string>(_description);

	const allowMulti = true;

	const dispatch = useAppDispatch();

	const descriptionRef = useRef<HTMLInputElement | null>(null);

	const styling_overrides = value?.styling_overrides;
	const { titleOverrides } = getStylingOverrides(styling_overrides);

	// force title to update when language changes so we can force a re-render of text editors
	useEffect(() => {
		setTitle(_title);
		setDescription(_description);
	}, [_description, _title, language, translationUpdates]);

	useEffect(() => {
		if (!dragOverlay) {
			return;
		}

		document.body.style.cursor = "grabbing";

		return () => {
			document.body.style.cursor = "";
		};
	}, [dragOverlay]);

	const handleClick = (id: UniqueIdentifier) => () => {
		setActiveSection((prev) => {
			if (allowMulti) {
				if (prev.includes(id)) {
					return prev.filter((el) => el !== id);
				}
				return [...prev, id];
			}
			if (prev.includes(id)) {
				return [];
			}
			return [id];
		});
	};

	const handleTitleClick = (e: React.MouseEvent) => {
		e.stopPropagation(); // this prevents the parent button from being clicked so we don't open the accordion
	};

	const handleTitleChange = (value: string) => {
		setTitle(value);
	};

	const handleDescriptionChange = (value: string) => {
		setDescription(value);
	};

	const handleFocus = () => {
		dispatch(setActiveAccordionContainer(containerId));
	};

	const handleBlur = () => {
		dispatch(setActiveAccordionContainer(null));
		// update items
		onContentBlur?.({
			containerId,
			itemId: value.id,
			title,
			description,
		});
	};

	const handleRemoveItem = () => onRemove?.(value.id);

	const handleStyleOverrideChange = (overrides: TextOverrides) => {
		if (!module) return;

		const updatedModule = {
			...module,
			content: {
				...module.content,
				columns: {
					...module.content.columns,
					[containerId]: module.content.columns[containerId].map((item: AccordionItem) => {
						if (item.id === value.id) {
							return {
								...item,
								styling_overrides: {
									...item.styling_overrides,
									content: {
										...item.styling_overrides?.content,
										title: overrides,
									}
								}
							};
						}
						return item;
					})
				}
			}
		};

		dispatch(updateHomepageModule(updatedModule));
	};

	const itemOpen = activeSection.includes(value.id);
	const itemHeight = descriptionRef.current?.offsetHeight ? `${descriptionRef.current?.offsetHeight}px` : 'fit-content';

	return (
		<li
			key={value.id}
			className={classNames(
				`accordion-item-${value.id}`,
				styles.Wrapper,
				fadeIn && styles.fadeIn,
				sorting && styles.sorting,
				dragOverlay && styles.dragOverlay
			)}
			style={
				{
					transition: [transition]
						.filter(Boolean)
						.join(", "),
					"--translate-x": transform
						? `${Math.round(transform.x)}px`
						: undefined,
					"--translate-y": transform
						? `${Math.round(transform.y)}px`
						: undefined,
					"--index": index,
					"--color": color
				} as React.CSSProperties
			}
			ref={ref}
		>
			<div
				className={classNames(
					'accordion-item',
					styles.Item,
					dragging && styles.dragging,
					handle && styles.withHandle,
					dragOverlay && styles.dragOverlay,
					disabled && styles.disabled,
					color && styles.color
				)}
				data-cypress="draggable-item"
				{...(!handle ? listeners : undefined)}
				{...props}
				tabIndex={!handle ? 0 : undefined}
			>
				<div className={classNames(styles.itemInnerContainer, 'item-inner-container')}>
					{!onRemove || hideRemoveButton ? null : (
						<div className={styles.removeItem}>
							<button
								className="round"
								onClick={handleRemoveItem}
							>
								<Icon name={ICONS.TRASH_OUTLINE} size={12} color={COLORS.WHITE} />
							</button>
						</div>
					)}

					<div className={styles.itemContentContainer}>
						<EditableTextSlim
							tagType={'h4'}
							className={classNames('accordion-item-title', titleOverrides)}
							defaultColor={ColorKeys.bodyTextColor}
							onEditableClick={handleTitleClick}
							value={title}
							onTextChange={handleTitleChange}
							onChange={() => null} /* because we're using `onTextChange` we really don't need this, but it's a required prop */
							onOverrideChange={handleStyleOverrideChange}
							onFocus={handleFocus}
							onBlur={handleBlur}
						/>

						<button
							className={classNames('clear no-style expand-accordion', styles.itemDropdownBtn)}
							onClick={handleClick(value.id)}
						>
							<span>
								{
									itemOpen
										? <Icon name={ICONS.KEYBOARD_ARROW_UP} color={''} size={20} />
										: <Icon name={ICONS.KEYBOARD_ARROW_DOWN} color={''} size={20} />
								}
							</span>
						</button>
					</div>
					<AnimatePresence>
						<LazyMotion features={domAnimation} key={value.id}> {/* even though this is not in a loop, it still needs a key prop */}
							<motion.div
								initial={{ height: 0, overflow: "hidden" }}
								animate={
									activeSection.includes(value.id)
										? {
											height: itemHeight,
											overflow: "visible"
										}
										: {}
								}
								exit={{ height: 0, overflow: "hidden" }}
							>
								<div className="editor-description-wrapper" ref={descriptionRef}>
									<EditableTextDraftQuill
										value={description}
										className={'accordion-item-description'}
										onChange={handleDescriptionChange}
										useNew
										onFocus={handleFocus}
										onBlur={handleBlur}
										displayOnLoading
									/>
								</div>
							</motion.div>
						</LazyMotion>
					</AnimatePresence>
				</div>
			</div>
		</li >
	);
}
)
);

Item.displayName = 'Item';

export default Item;
